import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

const IOS = {
  purple:'#5E5CE6', orange:'#FF9500', blue:'#007AFF',
  red:'#FF3B30', teal:'#5AC8FA',
};

const imagenes = [
  '/imagenes/GATO_404.png',
];

const NotFound = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [bgImg,   setBgImg]   = useState('');
  const [glitch,  setGlitch]  = useState(false);

  useEffect(() => {
    setBgImg(imagenes[Math.floor(Math.random() * imagenes.length)]);
  }, []);

  // Efecto glitch ocasional en el 404
  useEffect(() => {
    const t = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 200);
    }, 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <Box sx={{
      minHeight:'100vh', width:'100vw', position:'relative',
      backgroundImage:`url(${bgImg})`,
      backgroundSize:'cover', backgroundPosition:'center', backgroundRepeat:'no-repeat',
      display:'flex', alignItems:'center',
      justifyContent:{ xs:'center', md:'flex-start' },
      pl:{ xs:0, md:'6vw' }, px:{ xs:2, sm:0 },
    }}>
      {/* Overlay más oscuro — ambiente de error */}
      <Box sx={{
        position:'absolute', inset:0, zIndex:1,
        background:{
          xs:'rgba(0,0,0,0.65)',
          md:'linear-gradient(105deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.18) 100%)'
        }
      }} />

      {/* Contenido */}
      <Box sx={{ position:'relative', zIndex:2, width:{ xs:'100%', sm:480 }, maxWidth:{ xs:'100%', sm:480 } }}>

        {/* Logo */}
        <Box sx={{ mb:5 }}>
          <img src="/imagenes/logo-integra.png" alt="integra"
            style={{ height:32, display:'block', opacity:0.8 }}
            onError={e=>e.target.style.display='none'} />
        </Box>

        {/* 404 grande con glitch */}
        <Box sx={{ mb:2, position:'relative' }}>
          <Typography sx={{
            fontSize:{ xs:'7rem', sm:'9rem' }, fontWeight:900,
            lineHeight:1, letterSpacing:'-0.06em',
            color:'transparent',
            WebkitTextStroke:`2px ${glitch ? IOS.red : 'rgba(255,255,255,0.15)'}`,
            transition:'all 0.1s',
            userSelect:'none',
            position:'relative',
            '&::before': glitch ? {
              content:'"404"',
              position:'absolute', top:2, left:4,
              color:'transparent',
              WebkitTextStroke:`2px ${IOS.teal}`,
              opacity:0.8,
            } : {},
          }}>
            404
          </Typography>
          {/* Línea roja debajo del 404 */}
          <Box sx={{ height:3, borderRadius:20, mt:-1, mb:3,
            background:`linear-gradient(90deg, ${IOS.red}, ${IOS.orange}60, transparent)`,
            width:'70%'
          }} />
        </Box>

        {/* Título */}
        <Box sx={{ mb:3 }}>
          <Typography sx={{
            fontSize:{ xs:'2.2rem', sm:'2.8rem' }, fontWeight:800,
            color:'white', lineHeight:1.1, letterSpacing:'-0.03em'
          }}>
            Página no
          </Typography>
          <Typography sx={{
            fontSize:{ xs:'2.2rem', sm:'2.8rem' }, fontWeight:800,
            color:IOS.red, lineHeight:1.1, letterSpacing:'-0.03em'
          }}>
            encontrada.
          </Typography>
          <Typography sx={{ fontSize:'0.9rem', color:'rgba(255,255,255,0.45)', mt:1.5, lineHeight:1.6 }}>
            La ruta{' '}
            <Box component="span" sx={{ fontFamily:'monospace', fontSize:'0.82rem',
              px:0.8, py:0.2, borderRadius:'6px',
              bgcolor:'rgba(255,59,48,0.15)', color:IOS.red,
              border:'1px solid rgba(255,59,48,0.25)' }}>
              {location.pathname}
            </Box>
            {' '}no existe o fue movida
          </Typography>
        </Box>

        {/* Card de sugerencia */}
        <Box sx={{
          p:2, mb:4, borderRadius:'14px',
          bgcolor:'rgba(255,255,255,0.05)',
          border:'1px solid rgba(255,255,255,0.1)',
          backdropFilter:'blur(8px)',
          display:'flex', alignItems:'flex-start', gap:1.5
        }}>
          <Box sx={{ width:28, height:28, borderRadius:'8px', bgcolor:'rgba(255,59,48,0.15)',
            border:'1px solid rgba(255,59,48,0.25)', display:'flex', alignItems:'center',
            justifyContent:'center', flexShrink:0, mt:0.2 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </Box>
          <Box>
            <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:'rgba(255,255,255,0.7)', mb:0.3 }}>
              ¿Qué puedes hacer?
            </Typography>
            <Typography sx={{ fontSize:'0.76rem', color:'rgba(255,255,255,0.4)', lineHeight:1.6 }}>
              Verifica que la URL sea correcta, regresa al inicio o usa el botón de volver.
            </Typography>
          </Box>
        </Box>

        {/* Botones */}
        <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap' }}>
          <Box component="button" onClick={()=>navigate(-1)}
            sx={{
              height:46, px:3, borderRadius:'12px', border:'1.5px solid rgba(255,255,255,0.2)',
              bgcolor:'rgba(255,255,255,0.08)', cursor:'pointer', backdropFilter:'blur(8px)',
              display:'flex', alignItems:'center', gap:1, transition:'all 0.2s',
              '&:hover':{ bgcolor:'rgba(255,255,255,0.14)', borderColor:'rgba(255,255,255,0.35)' }
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            <Typography sx={{ fontSize:'0.85rem', fontWeight:600, color:'rgba(255,255,255,0.7)' }}>Volver</Typography>
          </Box>
          <Box component="button" onClick={()=>navigate('/dashboard')}
            sx={{
              height:46, px:3, borderRadius:'12px', border:'none',
              bgcolor:IOS.purple, cursor:'pointer',
              display:'flex', alignItems:'center', gap:1, transition:'all 0.2s',
              boxShadow:`0 6px 20px ${IOS.purple}50`,
              '&:hover':{ filter:'brightness(1.1)', transform:'translateY(-1px)' }
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            <Typography sx={{ fontSize:'0.85rem', fontWeight:700, color:'white' }}>Ir al inicio</Typography>
          </Box>
          <Box component="button" onClick={()=>navigate('/login')}
            sx={{
              height:46, px:3, borderRadius:'12px',
              border:`1.5px solid ${IOS.red}40`,
              bgcolor:`${IOS.red}12`, cursor:'pointer',
              display:'flex', alignItems:'center', gap:1, transition:'all 0.2s',
              '&:hover':{ bgcolor:`${IOS.red}22`, borderColor:`${IOS.red}70` }
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <Typography sx={{ fontSize:'0.85rem', fontWeight:600, color:IOS.red }}>Login</Typography>
          </Box>
        </Box>

        {/* Línea divisoria */}
        <Box sx={{ mt:6, display:'flex', alignItems:'center', gap:1.5 }}>
          <Box sx={{ flex:1, height:'1px', bgcolor:'rgba(255,255,255,0.08)' }} />
          <Typography sx={{ fontSize:'0.65rem', color:'rgba(255,255,255,0.2)', fontWeight:500 }}>Un bit a la vez.</Typography>
          <Box sx={{ flex:1, height:'1px', bgcolor:'rgba(255,255,255,0.08)' }} />
        </Box>
      </Box>

      {/* Logo ARDATA */}
      <Box sx={{ position:'fixed', bottom:{ xs:12, sm:20 }, right:{ xs:16, sm:24 }, zIndex:10,
        textAlign:'right', display:'flex', flexDirection:'column', alignItems:'flex-end', gap:0.5 }}>
        <img src="/imagenes/logo-ardata.png" alt="ARDATA"
          style={{ width:window.innerWidth<600?65:82, height:'auto', opacity:0.4 }}
          onError={e=>e.target.style.display='none'} />
        <Typography sx={{ fontSize:{ xs:'7px', sm:'8px' }, color:'rgba(255,255,255,0.25)' }}>
          © 2026 ardata, todos los derechos reservados
        </Typography>
      </Box>
    </Box>
  );
};

export default NotFound;
